import { type Game, getVariant } from '@chessbut/engine';
import type { Me } from '@chessbut/shared';
import { useEffect, useState } from 'react';
import { Flag, Share2 } from 'lucide-react';
import { GameView, verdictFor } from '../components/GameView.tsx';
import { materialSummary } from '../components/PlayerBar.tsx';
import { toast } from '../components/Toast.tsx';
import { LocalGame, saveKeyFor, useLocalGame } from '../game/localGame.ts';
import { retryable, submitResult } from '../game/uploads.ts';
import { ApiError } from '../lib/api.ts';
import { Link } from '../lib/router.tsx';
import { useSession } from '../lib/session.ts';
import { share } from '../lib/share.ts';
import { openAuth } from '../lib/ui.ts';
import { type ChallengeInfo, useChallenge } from './Home.tsx';
import { botName } from './LocalGameScreen.tsx';
import { SITE_URL } from '../lib/platform.ts';

/**
 * One square per 4 plies, coloured by who was ahead on material at that point:
 * 🟩 you, 🟨 level, 🟥 the bot. Ends with the result.
 */
export function challengeGrid(game: Game, outcome: 'win' | 'draw' | 'loss'): string {
  const start = game.positionAt(0);
  const cells: string[] = [];
  for (let ply = 4; ply <= game.ply; ply += 4) {
    const { balance } = materialSummary(game.positionAt(ply), start);
    cells.push(balance > 0 ? '🟩' : balance < 0 ? '🟥' : '🟨');
  }
  const rows: string[] = [];
  for (let i = 0; i < cells.length; i += 10) rows.push(cells.slice(i, i + 10).join(''));
  return `${rows.join('\n')}${rows.length ? ' ' : ''}${outcome === 'win' ? '🏆' : outcome === 'draw' ? '🤝' : '💀'}`;
}

export function challengeShareText(info: ChallengeInfo, game: Game | null, outcome: 'win' | 'draw' | 'loss', plies: number): string {
  const v = getVariant(info.variant)!;
  const bot = botName(info.variant, info.level);
  const moves = Math.ceil(plies / 2);
  const line = outcome === 'win' ? `🎯 beat ${bot} in ${moves}` : outcome === 'draw' ? `🎯 drew ${bot} in ${moves}` : `🎯 lost to ${bot} in ${moves}`;
  return [`ChessBut · Day ${info.dayNumber} ${v.info.emoji}`, `chess, but ${v.info.but}`, line, game ? challengeGrid(game, outcome) : ''].filter(Boolean).join('\n');
}

export function ChallengeScreen() {
  const info = useChallenge();
  if (!info) return <div className="page loading"><span className="spinner big" /></div>;
  if (!info.available) {
    return (
      <div className="page center">
        <h1>No challenge today</h1>
        <p className="muted">Check back when the next variant drops.</p>
      </div>
    );
  }
  return <ChallengeGame key={info.day} info={info} />;
}

function ChallengeGame({ info }: { info: ChallengeInfo }) {
  const key = saveKeyFor({ variant: info.variant, mode: 'challenge', day: info.day });
  const [practice, setPractice] = useState(false);
  const [lg, setLg] = useState(
    () =>
      LocalGame.restore(key) ??
      new LocalGame({ variant: info.variant, mode: 'challenge', human: 0, level: info.level, day: info.day, seed: info.seed }),
  );
  const snap = useLocalGame(lg);
  const { me } = useSession();
  const [result, setResult] = useState<{ counted: boolean; points?: number } | null>(null);
  const [confirmResign, setConfirmResign] = useState(false);
  useEffect(() => {
    if (!confirmResign) return;
    const t = setTimeout(() => setConfirmResign(false), 3000);
    return () => clearTimeout(t);
  }, [confirmResign]);

  useEffect(() => {
    lg.resume();
    return () => lg.dispose();
  }, [lg]);

  const status = lg.status();
  const outcome = !status.over || status.winner === undefined ? 'draw' : status.winner === 0 ? 'win' : 'loss';

  useEffect(() => {
    if (!status.over || lg.reported || status.reason === 'aborted') return;
    lg.markReported();
    submitResult<{ counted: boolean; points?: number; me: Me }>('/api/challenge', {
      day: info.day,
      moves: lg.game.history.map((r) => r.uci),
      result: status.result,
      reason: status.reason,
    })
      .then((r) => setResult({ counted: r.counted, points: r.points }))
      .catch((err) => {
        // Still queued (offline, server busy, or signed out): it goes through later.
        const queued = retryable(err) || (err instanceof ApiError && err.status === 401);
        toast(queued ? 'Saved on this device. Your result will be sent when you’re back online.' : (err as Error).message);
      });
  }, [status.over, snap.version]);

  const bot = botName(info.variant, info.level);
  const first = info.attempt ?? (result?.counted ? { result: outcome, plies: lg.game.ply } : null);
  const shareResult = () => {
    const a = info.attempt;
    const text = a && !result?.counted
      ? challengeShareText(info, null, a.result as 'win' | 'draw' | 'loss', a.plies)
      : challengeShareText(info, lg.game, outcome, lg.game.ply);
    void share({ kind: 'challenge', text, url: `${SITE_URL}/${info.dayNumber}` });
  };

  const again = () => {
    LocalGame.clear(key);
    setResult(null);
    setPractice(true);
    setLg(new LocalGame({ variant: info.variant, mode: 'challenge', human: 0, level: info.level, day: info.day, seed: info.seed }));
  };

  const grid = challengeGrid(lg.game, outcome);
  return (
    <GameView
      game={lg.game}
      version={snap.version}
      white={{ name: me?.name ?? 'You', username: me?.username, isYou: true, streak: me?.streak.current || undefined }}
      black={{ name: bot, rating: 'daily bot' }}
      perspective={0}
      you={0}
      movable={(c) => lg.canMove(c)}
      status={status}
      thinking={snap.thinking ? `${bot} is thinking…` : null}
      onMove={(uci) => lg.move(uci)}
      mode={`daily challenge${practice || info.attempt ? ' · practice' : ''}`}
      backTo="/"
      notices={
        !status.over && (info.attempt || practice) ? (
          <div className="notice">Practice round: your first result today already counts.</div>
        ) : !status.over ? (
          <div className="notice">Everyone faces the same bot today. Win in as few moves as you can. First try counts!</div>
        ) : null
      }
      actions={
        !status.over ? (
          <button className={`btn ${confirmResign ? 'danger' : ''}`} onClick={() => (confirmResign ? lg.resign() : setConfirmResign(true))}>
            <Flag className="icon" />
            {confirmResign ? 'Sure?' : 'Resign'}
          </button>
        ) : (
          <button className="btn" onClick={shareResult}>
            <Share2 className="icon" />
            Share
          </button>
        )
      }
      end={
        status.over
          ? {
              verdict: verdictFor(status, 0),
              subtitle: outcome === 'win' ? `You beat ${bot} in ${Math.ceil(lg.game.ply / 2)} moves` : undefined,
              gains: result?.counted ? { points: result.points ?? null, streak: me?.streak.current ?? null } : undefined,
              share: info.attempt && !result?.counted ? undefined : challengeShareText(info, lg.game, outcome, lg.game.ply),
              extra: (
                <>
                  {!result?.counted && !info.attempt && <pre className="share-preview">{grid}</pre>}
                  {result && !result.counted && first && (
                    <p className="end-note">
                      Practice game. Your counted result today: {first.result === 'win' ? `won in ${Math.ceil(first.plies / 2)} moves` : first.result}.
                    </p>
                  )}
                  {me?.guest && result?.counted && (
                    <button className="btn strong block" onClick={() => openAuth('signup', 'Create an account to put your challenge result on the leaderboard.')}>
                      Get on the challenge leaderboard
                    </button>
                  )}
                </>
              ),
              actions: (
                <>
                  <button className="btn primary big block" onClick={shareResult}>
                    <Share2 className="icon" />
                    Share result
                  </button>
                  <div className="two-up">
                    <Link to="/leaderboard?tab=challenge" className="btn small">
                      Leaderboard
                    </Link>
                    <button className="btn small" onClick={again}>
                      Practice
                    </button>
                  </div>
                </>
              ),
            }
          : null
      }
    />
  );
}
